/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { Reservation } from 'types/entities/Reservation'
import { CompanyContact } from 'types/entities/CompanyContact'
import { getCompanyDisplayName } from 'utils/companyUtils'

const buildPersonName = (firstName?: string | null, lastName?: string | null): string =>
    [firstName, lastName]
        .map((value) => value?.trim())
        .filter((value): value is string => Boolean(value))
        .join(' ')

export const resolveReservationContact = (reservation: Reservation): CompanyContact | null => {
    if (reservation.contact) {
        return reservation.contact
    }

    const companyContacts = reservation.company?.contacts ?? []
    if (companyContacts.length === 0) {
        return null
    }

    if (reservation.contact_id) {
        const matchedContact = companyContacts.find((contact) => contact.id === reservation.contact_id)
        if (matchedContact) {
            return matchedContact
        }
    }

    const defaultContact = companyContacts.find(
        (contact) => contact.id === reservation.company?.default_contact_id
    )

    return defaultContact ?? companyContacts[0] ?? null
}

export const getReservationAccountName = (reservation: Reservation): string => {
    const accountName =
        getCompanyDisplayName(reservation.company) ||
        getCompanyDisplayName(reservation.packagesCart?.company) ||
        reservation.packagesCart?.customer?.name?.trim() ||
        getCompanyDisplayName(reservation.packagesCart?.customer)

    return accountName || '---'
}

/** Contact / checkout customer person fields — never derived from account name. */
export const getReservationContactFirstName = (reservation: Reservation): string => {
    const contact = resolveReservationContact(reservation)
    const fromContact = contact?.first_name?.trim()
    if (fromContact) {
        return fromContact
    }

    return reservation.packagesCart?.customer?.first_name?.trim() || ''
}

export const getReservationContactLastName = (reservation: Reservation): string => {
    const contact = resolveReservationContact(reservation)
    const fromContact = contact?.last_name?.trim()
    if (fromContact) {
        return fromContact
    }

    return reservation.packagesCart?.customer?.last_name?.trim() || ''
}

export const getReservationContactName = (reservation: Reservation): string => {
    const fromContact = buildPersonName(
        getReservationContactFirstName(reservation),
        getReservationContactLastName(reservation)
    )
    if (fromContact) {
        return fromContact
    }

    return reservation.name?.trim() || '---'
}

export const getReservationContactEmail = (reservation: Reservation): string => {
    const contact = resolveReservationContact(reservation)

    return (
        contact?.email?.trim() ||
        reservation.packagesCart?.customer?.email?.trim() ||
        reservation.company?.email?.trim() ||
        reservation.email?.trim() ||
        '---'
    )
}

export const getReservationContactPhone = (reservation: Reservation): string => {
    const contact = resolveReservationContact(reservation)

    return (
        contact?.phone?.trim() ||
        reservation.packagesCart?.customer?.phone?.trim() ||
        reservation.company?.phone?.trim() ||
        '---'
    )
}

export const getReservationTime = (reservation: Reservation): string | null => {
    const timeMatch = reservation.time
        ?.trim()
        .match(/(?:^|T|\s)([01]?\d|2[0-3]):([0-5]\d)/)

    if (timeMatch) {
        return `${timeMatch[1].padStart(2, '0')}:${timeMatch[2]}`
    }

    if (
        typeof reservation.hour === 'number' &&
        typeof reservation.minute === 'number'
    ) {
        return `${String(reservation.hour).padStart(2, '0')}:${String(
            reservation.minute
        ).padStart(2, '0')}`
    }

    return null
}
